// ============================================================================
// JARVISTRAVEL - FLIGHTS STORE (demo persistence)
// Local persistence for flights the user actually told us about — planner
// intake, manual adds, confirmations — so demo mode survives reloads. When a
// trip has stored flights they REPLACE the synthesized demo bundle (the user's
// real flights beat invented ones). Live mode never touches this: the backend
// owns flight storage. JT-FL-005.
// ============================================================================

import type { TripFlight, TripFlights } from '@/app/types';

const KEY_PREFIX = 'jarvis_flights_v1:';
const key = (tripId: string) => `${KEY_PREFIX}${tripId}`;

/** Element-level shape check — downstream code indexes `legs[0].departure`. */
function isStoredFlight(v: unknown): v is TripFlight {
  if (!v || typeof v !== 'object') return false;
  const f = v as { id?: unknown; confirmState?: unknown; legs?: unknown };
  return typeof f.id === 'string'
    && typeof f.confirmState === 'string'
    && Array.isArray(f.legs)
    && f.legs.every((l) => {
      const leg = l as { flightNumber?: unknown; departure?: { scheduled?: unknown }; arrival?: { scheduled?: unknown } };
      return typeof leg?.flightNumber === 'string'
        && typeof leg?.departure?.scheduled === 'string'
        && typeof leg?.arrival?.scheduled === 'string';
    });
}

/** Stored flights for a trip, or null when none are stored or all are corrupt.
 *  Partially-corrupt entries are filtered out rather than crashing consumers.
 *  An empty list is kept: it's a trip whose flights were all removed. */
export function readStoredFlights(tripId: string): TripFlight[] | null {
  try {
    const raw = localStorage.getItem(key(tripId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const valid = parsed.filter(isStoredFlight);
    return valid.length > 0 || parsed.length === 0 ? valid : null;
  } catch {
    return null; // corrupt entry — fall back to the synthesized bundle
  }
}

export function writeStoredFlights(tripId: string, flights: TripFlight[]): void {
  try {
    localStorage.setItem(key(tripId), JSON.stringify(flights));
  } catch {
    /* quota/unavailable — demo persistence is best-effort */
  }
}

/** Remove a trip's stored flights. Best-effort — used to clear demo data when a
 *  demo starts and ends (JAR-1992). */
export function clearStoredFlights(tripId: string): void {
  try {
    localStorage.removeItem(key(tripId));
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/** Add or replace one flight; returns the new list (also persisted). */
export function upsertStoredFlight(tripId: string, flight: TripFlight): TripFlight[] {
  const list = readStoredFlights(tripId) ?? [];
  const next = [...list.filter((f) => f.id !== flight.id), flight];
  writeStoredFlights(tripId, next);
  return next;
}

/** Patch one stored flight in place (no-op when the trip has no store). */
export function patchStoredFlight(tripId: string, flightId: string, patch: Partial<TripFlight>): void {
  const list = readStoredFlights(tripId);
  if (!list) return;
  writeStoredFlights(tripId, list.map((f) => (f.id === flightId ? { ...f, ...patch } : f)));
}

/** Wrap stored flights into the bundle shape the seam returns. */
export function storedBundle(tripId: string, flights: TripFlight[]): TripFlights {
  return { tripId, flights, updatedAt: new Date().toISOString() };
}
